/**
 * Path→table registry for the Postgres {@link DocStorePort} adapter.
 *
 * The port addresses documents by `/`-joined path (`creations/{id}/versions/{n}`);
 * Postgres addresses rows by table + key columns. Each registry entry declares,
 * for one collection, how a doc path maps to a row and how doc fields map to
 * columns. The adapter itself (postgresDocStore.ts) holds ONE generic code path
 * over these descriptors — per-collection behavior lives here as data, not as
 * forked logic (one-path rule).
 *
 * Field-name mapping is automatic camelCase↔snake_case; a descriptor only
 * declares the exceptions:
 *   - `flatten`:  nested object → column prefix (engine.provider → engine_provider)
 *   - `jsonb`:    fields stored whole as jsonb (settings, gates, players, …)
 *   - `derived`:  doc fields NOT stored (recomputed on read; writes ignored)
 *   - `extrasColumn`: open-ended overflow — unmapped fields land in this jsonb
 *     column on write and are spread back on read (Surface #2 aggregates/matches).
 *
 * Two VIRTUAL collections have no table of their own (normalized away by the
 * schema) and are special-cased by the adapter:
 *   - `slugs/{slug}`        — synthesized over creations(slug UNIQUE)
 *   - `viewShards/{0..9}`   — synthesized over creations.views_count
 */

/** Column key extracted from a doc path's id segments. */
export type KeyValues = Record<string, string | number | boolean>;

export interface TableDef {
  /** Postgres table name. */
  table: string;
  /**
   * Map the doc-id path segments (everything identifying one doc: parent ids +
   * own id) to key column values. Receives e.g. ['<creationId>', '<n>'] for
   * `creations/{creationId}/versions/{n}`.
   */
  keyOf(ids: string[]): KeyValues;
  /**
   * Optional parent-only key mapping for collection queries whose document id
   * expands into multiple SQL key columns. Without this override, query()
   * derives the parent scope by dropping docIdColumn from keyOf(parent + fake
   * id), which is sufficient for ordinary single-column document ids.
   */
  collectionKeyOf?(parentIds: string[]): KeyValues;
  /** The column whose value is the Firestore-equivalent doc id (for query()'s
   *  `{ id }` augmentation and claimNext-style flows). */
  docIdColumn: string;
  /** Nested-object flattening: docField → { nestedKey → column }. */
  flatten?: Record<string, Record<string, string>>;
  /** Doc fields stored whole as jsonb columns (column = snake_case(field)). */
  jsonb?: ReadonlySet<string>;
  /** Explicit string-valued JSON paths supported by query/count/order. This
   * does not imply numeric/object query semantics or flatten document writes. */
  jsonTextQueries?: ReadonlySet<string>;
  /** Doc fields never stored (derived on read / ignored on write). */
  derived?: ReadonlySet<string>;
  /** jsonb overflow column for unmapped fields (undefined = unmapped throws). */
  extrasColumn?: string;
  /** Explicit REAL-column allowlist (snake_case) for extras-bearing tables:
   *  any doc field mapping outside this set routes to extrasColumn — scalars
   *  included (writers set open-ended fields like ledger writtenBy). */
  columns?: ReadonlySet<string>;
  /** Columns injected on INSERT from the path key when the doc data doesn't
   *  carry them (e.g. versions rows need creation_id from the path). keyOf
   *  already returns these; listed here to include them in upserts. */
}

export const TABLES: Record<string, TableDef> = {
  matchFinalizations: {
    table: 'match_finalizations', keyOf: ([id]) => ({ id }), docIdColumn: 'id',
    jsonb: new Set(['payload', 'outcome']),
  },
  buildPipelines: {
    table: 'build_pipeline_catalog', keyOf: ([id]) => ({ id }), docIdColumn: 'id',
    jsonb: new Set(['entries']),
  },
  // ---- Surface #1: creator platform -------------------------------------
  creations: {
    table: 'creations',
    keyOf: ([id]) => ({ id }),
    docIdColumn: 'id',
    flatten: { engine: { provider: 'engine_provider', model: 'engine_model' } },
    jsonb: new Set([
      'settings',
      'assets',
      'sfxDraft',
      'forkedFrom',
      'gameDesign',
      'analysisDraft',
      'conceptManualDraft',
      'referenceBriefs',
      'videoBriefs',
      'editorsPick',
      'rating',
      'avoidDirection',
      'compositionConversion',
      'composedRevisionRun',
      'titleAuthority',
    ]),
    // editors/memberUids live in creation_members. The source epoch is read
    // from the row but computed only by its trigger, including during upsert.
    derived: new Set(['editors', 'memberUids', 'compositionIntentSourceRev']),
  },
  'creations/versions': {
    table: 'creation_versions',
    keyOf: ([creationId, n]) => ({ creation_id: creationId, n: Number(n) }),
    docIdColumn: 'n',
    flatten: { tokenUsage: { input: 'token_input', output: 'token_output' } },
    jsonb: new Set([
      'gates',
      'settingsSnapshot',
      'pipelineSnapshot',
      'qualityRun',
      'qualitySummary',
      'executionPolicy',
      'designSnapshot',
      'manifestSummary',
      'assetsSnapshot',
      'assetGen',
      'artCompletionAssets',
      'presentationEdit',
      'visualMigration',
      'platformerMechanicOutcomes',
      'platformerPrimaryActorAssetIssues',
      'buildProgress',
      'buildTiming',
      'creationRun',
      'runtimeContract',
      'capabilityLock',
      'compositionPublicLaunchV2',
      'creationFilmManifest',
    ]),
  },
  creationFilmRuns: {
    table: 'creation_film_runs',
    keyOf: ([runId]) => ({ run_id: runId }),
    docIdColumn: 'run_id',
    jsonb: new Set(['binding', 'failure']),
  },
  creationFilmMaterials: {
    table: 'creation_film_materials',
    keyOf: ([id]) => ({ id }),
    docIdColumn: 'id',
    jsonb: new Set(['strip']),
  },
  creationArtifactRuns: {
    table: 'creation_artifact_runs',
    keyOf: ([runId]) => ({ run_id: decodeURIComponent(runId) }),
    docIdColumn: 'run_id',
    jsonb: new Set(['moments']),
  },
  creationArtifacts: {
    table: 'creation_artifacts',
    keyOf: ([artifactId]) => ({ artifact_id: decodeURIComponent(artifactId) }),
    docIdColumn: 'artifact_id',
    jsonb: new Set(['payload', 'contentDigest', 'provenance', 'roleHints', 'quality', 'safety']),
  },
  creationArtifactEdges: {
    table: 'creation_artifact_edges',
    keyOf: ([edgeId]) => ({ edge_id: decodeURIComponent(edgeId) }),
    docIdColumn: 'edge_id',
  },
  creationArtifactUsages: {
    table: 'creation_artifact_usages',
    keyOf: ([usageId]) => ({ usage_id: decodeURIComponent(usageId) }),
    docIdColumn: 'usage_id',
  },
  creationProviderDispatches: {
    table: 'creation_provider_dispatches',
    keyOf: ([dispatchId]) => ({ dispatch_id: decodeURIComponent(dispatchId) }),
    docIdColumn: 'dispatch_id',
  },
  creationImageBudgets: {
    table: 'creation_image_budgets',
    keyOf: ([runId]) => ({ run_id: decodeURIComponent(runId) }),
    docIdColumn: 'run_id',
    jsonb: new Set(['reservedSlots']),
  },
  presentationRepublishRequests: {
    table: 'presentation_republish_requests',
    keyOf: ([id]) => ({ id }),
    docIdColumn: 'id',
  },
  'creations/likes': {
    table: 'likes',
    keyOf: ([creationId, uid]) => ({ creation_id: creationId, uid }),
    docIdColumn: 'uid',
  },
  'creations/comments': {
    table: 'comments',
    keyOf: ([creationId, commentId]) => ({ creation_id: creationId, id: commentId }),
    docIdColumn: 'id',
  },
  // First-draft keep/discard verdicts (0077): doc id `${versionN}:${respondentUid}`
  // — the server builds it; the row's own `id` column stores it verbatim.
  'creations/feedback': {
    table: 'creation_feedback',
    keyOf: ([creationId, id]) => ({ creation_id: creationId, id }),
    docIdColumn: 'id',
  },
  'creations/viewers': {
    table: 'creation_viewers',
    keyOf: ([creationId, uid]) => ({ creation_id: creationId, uid }),
    docIdColumn: 'uid',
  },
  'creations/assetVariants': {
    table: 'asset_variants',
    keyOf: ([creationId, variantId]) => ({ creation_id: creationId, variant_id: variantId }),
    docIdColumn: 'variant_id',
    // Strip-kind variants carry their cell geometry + identity whole (0038);
    // audio candidates carry inspected metadata (0076) and, when a provider
    // generated them, its provenance (0077). Generated sprites preserve their
    // immutable direction/alignment contract as one JSON value (0107).
    // rawPreviewUrl is scalar text, mapped normally to raw_preview_url (0143).
    jsonb: new Set(['strip', 'audio', 'generator', 'orientation']),
  },
  'creations/workshop': {
    table: 'creation_workshops',
    keyOf: ([creationId, id]) => ({ creation_id: creationId, id }),
    docIdColumn: 'id',
    jsonb: new Set(['state']),
  },
  'creations/assetRejections': {
    table: 'asset_rejections',
    keyOf: ([creationId, digest]) => ({ creation_id: creationId, content_sha256: digest }),
    docIdColumn: 'content_sha256',
    jsonb: new Set(['record']),
  },
  'creations/assetRejectionHistory': {
    table: 'asset_rejection_history',
    keyOf: ([creationId, revision]) => ({ creation_id: creationId, revision: Number(revision) }),
    docIdColumn: 'revision',
    jsonb: new Set(['record', 'priorRecord']),
  },
  buildJobs: {
    table: 'build_jobs',
    keyOf: ([id]) => ({ id }),
    docIdColumn: 'id',
    jsonb: new Set(['queueIntervals', 'measuredExecution', 'compositionV2Lifecycle']),
  },
  qualityRuns: {
    table: 'quality_runs',
    keyOf: ([runId]) => ({ run_id: runId }),
    docIdColumn: 'run_id',
    jsonb: new Set(['admission', 'specification', 'plan', 'executionCheckpoint', 'state']),
  },
  qualityTransitions: {
    table: 'quality_transition_events',
    keyOf: ([id]) => ({ id }),
    docIdColumn: 'id',
    jsonb: new Set(['claim']),
  },
  qualityEvidence: {
    table: 'quality_evidence',
    keyOf: ([id]) => ({ id }),
    docIdColumn: 'id',
    jsonb: new Set(['evidence', 'artifact']),
  },
  qualityReservations: {
    table: 'quality_provider_reservations',
    keyOf: ([reservationId]) => ({ reservation_id: reservationId }),
    docIdColumn: 'reservation_id',
    jsonb: new Set(['allowedRoutes', 'usage', 'response']),
  },
  qualityOutbox: {
    table: 'quality_outbox',
    keyOf: ([id]) => ({ id }),
    docIdColumn: 'id',
    jsonb: new Set(['payload']),
  },
  qualityManifests: {
    table: 'quality_manifests',
    keyOf: ([runId]) => ({ run_id: runId }),
    docIdColumn: 'run_id',
    jsonb: new Set(['claim', 'manifest', 'artifact']),
  },
  qualityPublications: {
    table: 'quality_publications',
    keyOf: ([runId]) => ({ run_id: runId }),
    docIdColumn: 'run_id',
    jsonb: new Set(['claim', 'source', 'dist', 'media']),
  },
  failedCandidates: {
    table: 'failed_candidates',
    keyOf: ([candidateId]) => ({ candidate_id: candidateId }),
    docIdColumn: 'candidate_id',
    jsonb: new Set(['candidate']),
  },
  candidateEligibility: {
    table: 'candidate_eligibility',
    keyOf: ([id]) => ({ id }),
    docIdColumn: 'id',
    extrasColumn: 'eligibility',
    columns: new Set(['id', 'candidate_id', 'assessed_at']),
  },
  candidateRechecks: {
    table: 'candidate_rechecks',
    keyOf: ([jobId]) => ({ job_id: jobId }),
    docIdColumn: 'job_id',
    extrasColumn: 'request',
    columns: new Set([
      'job_id', 'dedupe_key', 'candidate_id', 'status', 'claim_token',
      'lease_generation', 'lease_expiry', 'cancellation_requested',
      'created_at', 'finished_at',
    ]),
  },
  candidateRecheckResults: {
    table: 'candidate_recheck_results',
    keyOf: ([jobId]) => ({ job_id: jobId }),
    docIdColumn: 'job_id',
    jsonb: new Set(['result']),
  },
  candidateRecheckLimits: {
    table: 'candidate_recheck_limits',
    keyOf: ([key]) => ({ key }),
    docIdColumn: 'key',
    extrasColumn: 'value',
    columns: new Set(['key']),
  },
  candidatePreviewGrants: {
    table: 'candidate_preview_grants',
    keyOf: ([tokenHash]) => ({ token_hash: tokenHash }),
    docIdColumn: 'token_hash',
  },
  candidatePreviewSessions: {
    table: 'candidate_preview_sessions',
    keyOf: ([sessionHash]) => ({ session_hash: sessionHash }),
    docIdColumn: 'session_hash',
  },
  candidatePreviewBuildCandidates: {
    table: 'candidate_preview_build_candidates',
    keyOf: ([candidateId]) => ({ candidate_id: candidateId }),
    docIdColumn: 'candidate_id',
    jsonb: new Set(['candidate']),
  },
  qualityPlatformControl: {
    table: 'quality_platform_control',
    keyOf: () => ({ id: true }),
    docIdColumn: 'id',
  },
  qualityPlatformControlHistory: {
    table: 'quality_platform_control_history',
    keyOf: ([commandId]) => ({ command_id: commandId }),
    docIdColumn: 'command_id',
    jsonb: new Set(['control']),
  },
  compositionRuntimeControl: {
    table: 'composition_runtime_control',
    keyOf: () => ({ id: true }),
    docIdColumn: 'id',
    jsonb: new Set(['activeRuntime', 'admissionControls', 'cohortSubjectUids', 'executionRevocations']),
  },
  composeLibraryEntries: {
    table: 'compose_library_entries', keyOf: ([digest]) => ({ digest }), docIdColumn: 'digest',
    jsonb: new Set(['origin', 'body', 'evidence']),
  },
  compositionBuildsV2: {
    table: 'composition_builds_v2', keyOf: ([buildId]) => ({ build_id: buildId }),
    docIdColumn: 'build_id', jsonb: new Set(['accepted']),
  },
  compositionOnlineRendezvousV2: {
    table: 'composition_online_rendezvous_v2', keyOf: ([id]) => ({ id }), docIdColumn: 'id',
    jsonb: new Set(['payload']), jsonTextQueries: new Set(['payload.target.allocationId','payload.target.matchToken','payload.target.createClaim','payload.phase']),
  },
  compositionOnlineRendezvousQuotaV2: {
    table: 'composition_online_rendezvous_quota_v2', keyOf: ([id]) => ({ id }), docIdColumn: 'id',
    jsonb: new Set(['payload']),
  },
  compositionIntentReviewsV2: {
    table: 'composition_intent_reviews_v2', keyOf: ([id]) => ({ review_digest: id }), docIdColumn: 'review_digest',
    jsonb: new Set(['review']),
  },
  compositionIntentReviewConsumptionsV2: {
    table: 'composition_intent_review_consumptions_v2', keyOf: ([id]) => ({ proposal_id: id }), docIdColumn: 'proposal_id',
  },
  compositionIntentSpendBudgetsV2: {
    table: 'composition_intent_spend_budgets_v2', keyOf: ([id]) => ({ budget_id: id }), docIdColumn: 'budget_id',
    jsonb: new Set(['routes']),
  },
  compositionIntentSpendReservationsV2: {
    table: 'composition_intent_spend_reservations_v2', keyOf: ([id]) => ({ reservation_id: id }), docIdColumn: 'reservation_id',
    jsonb: new Set(['receipt']),
  },
  compositionGenerationSpendScopesV2: {
    table: 'composition_generation_spend_scopes_v2', keyOf: ([id]) => ({ scope_id: id }), docIdColumn: 'scope_id',
    jsonb: new Set(['parent']),
  },
  compositionGenerationSpendReservationsV2: {
    table: 'composition_generation_spend_reservations_v2', keyOf: ([id]) => ({ reservation_id: id }), docIdColumn: 'reservation_id',
    jsonb: new Set(['parent', 'transport', 'receipt']),
  },
  compositionGenerationCandidatesV2: {
    table: 'composition_generation_candidates_v2', keyOf: ([id]) => ({ id }), docIdColumn: 'id',
    jsonb: new Set(['parent', 'request', 'reply', 'attempt']),
  },
  compositionGenerationStagesV2: {
    table: 'composition_generation_stages_v2', keyOf: ([id]) => ({ id }), docIdColumn: 'id',
    jsonb: new Set(['position', 'outcome']),
  },
  compositionGenerationMaterializationsV2: {
    table: 'composition_generation_materializations_v2', keyOf: ([id]) => ({ id }), docIdColumn: 'id',
    jsonb: new Set(['materialization', 'materials', 'result']),
  },
  compositionIntentProposalsV2: {
    table: 'composition_intent_proposals_v2', keyOf: ([id]) => ({ proposal_id: id }), docIdColumn: 'proposal_id',
    jsonb: new Set(['source','calls','draft']),
  },
  compositionIntentProposalCommandsV2: {
    table: 'composition_intent_proposal_commands_v2', keyOf: ([id]) => ({ command_key: id }), docIdColumn: 'command_key',
  },
  compositionIntentProposalHeadsV2: {
    table: 'composition_intent_proposal_heads_v2', keyOf: ([creationId]) => ({ creation_id: creationId }), docIdColumn: 'creation_id',
  },
  compositionIntentProposalQuotasV2: {
    table: 'composition_intent_proposal_quotas_v2', keyOf: ([id]) => {
      const split = id.lastIndexOf('~'); return { uid: decodeURIComponent(id.slice(0, split)), day: id.slice(split + 1) };
    }, docIdColumn: 'uid', collectionKeyOf: () => ({}),
  },
  compositionCandidatesV2: {
    table: 'composition_candidates_v2',
    keyOf: ([id]) => { const [buildId, commandId] = id.split('~'); return { build_id: buildId, command_id: commandId }; },
    docIdColumn: 'command_id', jsonb: new Set(['payload', 'state']),
  },
  compositionGameValidationsV2: {
    table: 'composition_game_validations_v2', keyOf: ([digest]) => ({ id: digest }), docIdColumn: 'id',
    jsonb: new Set(['receipt','receiptRef','evidence','evidenceRef','webRef','descriptor']),
  },
  compositionPublicationsV2: {
    table: 'composition_publications_v2', keyOf: ([id]) => { const [buildId, commandId] = id.split('~'); return { build_id: buildId, command_id: commandId }; },
    docIdColumn: 'command_id', jsonb: new Set(['descriptor']),
  },
  compositionPrivateActiveV2: {
    table: 'composition_private_active_v2', keyOf: ([creationId]) => ({ creation_id: creationId }), docIdColumn: 'creation_id',
  },
  compositionPublicValidationsV2: {
    table: 'composition_public_validations_v2', keyOf: ([digest]) => ({ digest }), docIdColumn: 'digest',
    jsonb: new Set(['coverage']),
  },
  compositionPublicLaunchesV2: {
    table: 'composition_public_launches_v2', keyOf: ([id]) => {
      const split = id.lastIndexOf('~');
      return { creation_id: id.slice(0, split), version: Number(id.slice(split + 1)) };
    },
    docIdColumn: 'version', jsonb: new Set(['predecessor', 'artifacts', 'launch']),
  },
  compositionPublicActiveV2: {
    table: 'composition_public_active_v2', keyOf: ([creationId]) => ({ creation_id: creationId }), docIdColumn: 'creation_id',
  },
  compositionPublicCompletionsV2: {
    table: 'composition_public_completions_v2', keyOf: ([completionId]) => ({ completion_id: completionId }),
    docIdColumn: 'completion_id',
    jsonb: new Set(['target', 'privatePublication', 'privateCheckpoint', 'retry', 'publicPublication', 'exportedArtifacts']),
  },
  componentRegistryV2: {
    table: 'component_registry_v2',
    keyOf: () => ({ id: true }), // singleton row (`componentRegistryV2/global`)
    docIdColumn: 'id',
    jsonb: new Set(['registry']),
  },
  componentRegistryV2History: {
    table: 'component_registry_v2_history',
    keyOf: ([commandId]) => ({ command_id: commandId }),
    docIdColumn: 'command_id',
    jsonb: new Set(['operation', 'state']),
  },
  componentPromotionIssuancesV2: {
    table: 'component_promotion_issuances_v2',
    keyOf: ([digest]) => ({ digest }),
    docIdColumn: 'digest',
    jsonb: new Set(['artifact', 'receipt', 'source', 'executable', 'evidenceRef', 'integrationRef', 'mechanicalSpecRef']),
  },
  // Async asset-studio generation queue (0029). Top-level like buildJobs.
  assetGenJobs: {
    table: 'asset_gen_jobs',
    keyOf: ([id]) => ({ id }),
    docIdColumn: 'id',
    // Whole-json payloads carried by the typed queue row.
    jsonb: new Set(['completion', 'strip', 'autoAttachCover', 'titleOverlay', 'audioBrief', 'creationRun', 'plan', 'prerequisite', 'measuredBudget', 'deferredSlots']),
  },
  // On-demand rendered playtest audits (0036). assetGenJobs sibling; the
  // stamped PlaytestAuditReport rides a jsonb column.
  auditJobs: {
    table: 'audit_jobs',
    keyOf: ([id]) => ({ id }),
    docIdColumn: 'id',
    jsonb: new Set(['report']),
  },
  llmRequests: {
    table: 'llm_requests',
    keyOf: ([id]) => ({ id }),
    docIdColumn: 'id',
  },
  // Editors' Pick per-version review cache (0024): the 2-part composite row key
  // (creation_id, version) packs into ONE doc id `{creationId}:{version}` —
  // neither part can contain ':' (creation ids are uuid/`native-<slug>`-shaped,
  // version a number), so the split is unambiguous (userGameFinishes precedent).
  editorsPickReviews: {
    table: 'editors_pick_reviews',
    keyOf: ([id]) => {
      const [creationId, version] = id.split(':');
      return { creation_id: creationId, version: Number(version) };
    },
    docIdColumn: 'creation_id', // synthetic (creatorQuotas precedent): review flows never re-derive the doc id
    jsonb: new Set(['tags']),
  },
  portalSessions: {
    table: 'portal_sessions',
    keyOf: ([sessionId]) => ({ session_id: sessionId }),
    docIdColumn: 'session_id',
  },
  // Admin Stats snapshots (0026): one row per 4h window, keyed by the
  // grid-floored window_start (ms). quant/provenance are whole-jsonb blobs.
  adminStats: {
    table: 'admin_stats_snapshots',
    keyOf: ([windowStart]) => ({ window_start: Number(windowStart) }),
    docIdColumn: 'window_start',
    jsonb: new Set(['quant', 'provenance', 'narrativeBullets']),
  },
  // Online Arena invites (0024): top-level collection keyed by the pairing
  // token. All scalar columns → automatic camelCase↔snake_case; no jsonb/extras.
  matchInvites: {
    table: 'match_invites',
    keyOf: ([token]) => ({ token }),
    docIdColumn: 'token',
  },
  // Platform-wide social presence (0040): one row per user, keyed by uid.
  // All scalar columns → automatic camelCase↔snake_case; no jsonb/extras.
  userPresence: {
    table: 'user_presence',
    keyOf: ([uid]) => ({ uid }),
    docIdColumn: 'uid',
  },
  // The Clubhouse's global chat room (0041): flat top-level collection keyed by
  // message id — one room, so there is nothing to scope on. All scalar columns
  // → automatic camelCase↔snake_case; no jsonb/extras.
  chatMessages: {
    table: 'chat_messages',
    keyOf: ([id]) => ({ id }),
    docIdColumn: 'id',
  },
  actionLog: {
    table: 'action_log',
    keyOf: ([id]) => ({ id }),
    docIdColumn: 'id',
    jsonb: new Set(['details']),
  },
  creatorProfiles: {
    table: 'creator_profiles',
    keyOf: ([uid]) => ({ uid }),
    docIdColumn: 'uid',
    jsonb: new Set(['quotaOverrides']),
  },
  creatorQuotas: {
    table: 'creator_quotas',
    // doc id is `{uid}_{yyyy-mm-dd}`; uid may itself contain underscores, the
    // day never does — split at the LAST underscore.
    keyOf: ([id]) => {
      const cut = id.lastIndexOf('_');
      return { uid: id.slice(0, cut), day: id.slice(cut + 1) };
    },
    docIdColumn: 'uid', // synthetic; quota flows never re-derive the doc id
  },
  platformSettings: {
    table: 'platform_settings',
    keyOf: () => ({ id: true }), // singleton row (`platformSettings/global`)
    docIdColumn: 'id',
    flatten: {
      defaultEngine: { provider: 'default_engine_provider', model: 'default_engine_model' },
    },
    jsonb: new Set(['galleryFeaturedSlugs', 'editorsPick', 'audioGeneration', 'creationFeatures']),
  },
  platformAdminSettings: {
    table: 'platform_admin_settings',
    keyOf: () => ({ id: true }),
    docIdColumn: 'id',
    jsonb: new Set(['mediaReviewPilotCreationIds', 'measuredFirstPlayablePilotUids']),
  },

  // ---- Surface #2: telemetry / ranked / leaderboard ----------------------
  users: {
    table: 'platform_users',
    keyOf: ([uid]) => ({ uid }),
    docIdColumn: 'uid',
    extrasColumn: 'extras',
    columns: new Set(['uid', 'nickname', 'avatar', 'created_at']),
  },
  // The `users/*/games` collection GROUP (AdminDocStore groupIds {'games'}):
  // the leaderboard recompute queries group 'games' and filters game_id in the
  // spec; doc.id == gameId per CONTRACTS §4.
  games: {
    table: 'user_game_aggregates',
    keyOf: ([gameId]) => ({ game_id: gameId }),
    docIdColumn: 'game_id',
    extrasColumn: 'extras',
    columns: new Set(['uid','game_id','nickname','games_played','games_won','games_lost','games_draw','total_score','total_game_time_seconds','highest_single_game_score','ranked_mmr','ranked_rank','ranked_division','ranked_games','last_played_at']),
  },
  'users/games': {
    table: 'user_game_aggregates',
    keyOf: ([uid, gameId]) => ({ uid, game_id: gameId }),
    docIdColumn: 'game_id', // collection-group semantics: doc.id == gameId
    extrasColumn: 'extras',
    columns: new Set(['uid','game_id','nickname','games_played','games_won','games_lost','games_draw','total_score','total_game_time_seconds','highest_single_game_score','ranked_mmr','ranked_rank','ranked_division','ranked_games','last_played_at']),
  },
  // Badge engine (0023): earned badges, keyed like users/games — the uid rides
  // the path, the badge id is the doc id.
  'users/badges': {
    table: 'user_badges',
    keyOf: ([uid, badgeId]) => ({ uid, badge_id: badgeId }),
    docIdColumn: 'badge_id',
  },
  // Game-finish events (0023): 3-part composite row key packed into ONE doc id
  // `{gameKey}:{mode}:{day}` — none of the parts can contain ':' (gameKey is a
  // slug, uuid or draft-<hash>, mode an enum, day an ISO date), so the split is unambiguous.
  'users/gameFinishes': {
    table: 'user_game_finishes',
    keyOf: ([uid, id]) => {
      const [gameKey, mode, day] = id.split(':');
      return { uid, game_key: gameKey, mode, day };
    },
    collectionKeyOf: ([uid]) => ({ uid }),
    docIdColumn: 'game_key', // synthetic (creatorQuotas precedent): finish flows never re-derive the doc id
  },
  // Play history (0047): one row per (player, game) — the doc id IS the game
  // key, so no packing is needed (the gameFinishes 3-part key is the exception,
  // not the rule).
  'users/gamePlays': {
    table: 'game_plays',
    keyOf: ([uid, gameKey]) => ({ uid, game_key: gameKey }),
    docIdColumn: 'game_key',
  },
  'users/leaderboardFirsts': {
    table: 'user_leaderboard_firsts',
    keyOf: ([uid, gameId]) => ({ uid, game_id: gameId }),
    docIdColumn: 'game_id',
  },
  'users/chatPeers': {
    table: 'user_chat_peers',
    keyOf: ([uid, peerUid]) => ({ uid, peer_uid: peerUid }),
    docIdColumn: 'peer_uid',
  },
  'users/games/events': {
    table: 'user_game_events',
    keyOf: ([uid, gameId, id]) => ({ uid, game_id: gameId, id }),
    docIdColumn: 'id',
    extrasColumn: 'payload',
    columns: new Set(['id', 'uid', 'game_id', 'created_at']),
  },
  'users/games/ranked': {
    // ranked/profile — fixed doc id 'profile'; key is (uid, game_id)
    table: 'user_game_ranked',
    keyOf: ([uid, gameId]) => ({ uid, game_id: gameId }),
    docIdColumn: 'uid',
    extrasColumn: 'extras',
    columns: new Set(['uid','game_id','mmr','rank','division','updated_at']),
  },
  'games/matches': {
    table: 'matches',
    keyOf: ([gameId, matchId]) => ({ game_id: gameId, match_id: matchId }),
    docIdColumn: 'match_id',
    jsonb: new Set(['players', 'scores']),
    extrasColumn: 'extras',
    columns: new Set(['game_id','match_id','room_kind','seed','started_at_ms','ended_at_ms','duration_ms','players','scores','winner_slot','end_reason','ranked','created_at']),
  },
  'games/leaderboard': {
    // leaderboard/snapshot — fixed doc id 'snapshot'; key is game_id
    table: 'leaderboard_snapshots',
    keyOf: ([gameId]) => ({ game_id: gameId }),
    docIdColumn: 'game_id',
    jsonb: new Set(['rows']),
    columns: new Set(['game_id','rows','computed_at_ms','kind']),
  },
  'games/opponents': {
    table: 'head_to_head',
    // Legacy paths retain their exact original semantics. Never guess how to
    // split an old ambiguous key or silently relabel historical aggregate rows.
    keyOf: ([gameId, pair]) => {
      const cut = pair.indexOf('_');
      return { game_id: gameId, uid: pair.slice(0, cut), opponent_uid: pair.slice(cut + 1) };
    },
    collectionKeyOf: ([gameId]) => ({ game_id: gameId }),
    docIdColumn: 'uid',
    extrasColumn: 'extras',
    columns: new Set(['game_id','uid','opponent_uid','opponent_nickname','wins','losses','draws','total_games_vs','best_score_against','total_my_score','total_opp_score','last_played_at_ms']),
  },
  'games/opponentsV2': {
    table: 'head_to_head',
    // Separate transport namespace: legacy UIDs never reserved a prefix, so
    // sniffing a version inside an old document ID would misread valid users.
    keyOf: ([gameId, pair]) => {
      const parts = pair.split('|');
      if (parts.length !== 2 || parts.some(part => !part)) throw new Error('invalid head-to-head pair');
      return { game_id: gameId, uid: decodeURIComponent(parts[0]), opponent_uid: decodeURIComponent(parts[1]) };
    },
    collectionKeyOf: ([gameId]) => ({ game_id: gameId }),
    docIdColumn: 'uid',
    extrasColumn: 'extras',
    columns: new Set(['game_id','uid','opponent_uid','opponent_nickname','wins','losses','draws','total_games_vs','best_score_against','total_my_score','total_opp_score','last_played_at_ms']),
  },
  nicknames: {
    table: 'nicknames',
    keyOf: ([slug]) => ({ slug }),
    docIdColumn: 'slug',
    extrasColumn: 'extras',
    columns: new Set(['slug', 'owner_uid', 'nickname', 'claimed_at_ms']),
  },
  // _probe/{uid} — boot-time data-plane connectivity probe (merge + read-back)
  _probe: {
    table: 'boot_probes',
    keyOf: ([uid]) => ({ uid }),
    docIdColumn: 'uid',
  },
  'games/checkpoints': {
    table: 'match_checkpoints',
    keyOf: ([gameId, matchId]) => ({ game_id: gameId, match_id: matchId }),
    docIdColumn: 'match_id',
    jsonb: new Set(['blob']),
  },
  'games/replays': {
    table: 'replays',
    keyOf: ([gameId, matchId]) => ({ game_id: gameId, match_id: matchId }),
    docIdColumn: 'match_id',
    // the whole header doc is merge-written blob data (startedAt/status/result/…)
    extrasColumn: 'header',
    columns: new Set(['game_id', 'match_id']),
  },
  'games/replays/scrolls': {
    table: 'replay_scrolls',
    keyOf: ([gameId, matchId, scrollId]) => ({
      game_id: gameId,
      match_id: matchId,
      scroll_id: scrollId,
    }),
    docIdColumn: 'scroll_id',
    jsonb: new Set(['frames', 'events']),
  },
};

/** Virtual collections handled specially by the adapter (no table entry). */
export const VIRTUAL = {
  slugs: 'slugs',
  viewShards: 'viewShards',
} as const;

/**
 * Resolve a doc or collection path to its registry key + id segments.
 * `creations/abc/versions/3` → { key: 'creations/versions', ids: ['abc','3'] }.
 * Collection names occupy even positions, ids odd ones.
 */
export function resolvePath(path: string): { key: string; ids: string[] } {
  const seg = path.split('/').filter(Boolean);
  const collections: string[] = [];
  const idParts: string[] = [];
  for (let i = 0; i < seg.length; i++) {
    if (i % 2 === 0) collections.push(seg[i]);
    else idParts.push(seg[i]);
  }
  return { key: collections.join('/'), ids: idParts };
}

/** Parent-key filters for a nested collection query. Composite document ids
 * may expand to several SQL key columns, so their registry descriptor supplies
 * an explicit parent-only mapping instead of parsing query()'s fake doc id. */
export function collectionKeyValues(def: TableDef, idsWithFakeDocId: string[]): KeyValues {
  if (def.collectionKeyOf) return def.collectionKeyOf(idsWithFakeDocId.slice(0, -1));
  const keys = def.keyOf(idsWithFakeDocId);
  return Object.fromEntries(Object.entries(keys).filter(([column]) => column !== def.docIdColumn));
}

export const camelToSnake = (s: string): string =>
  s.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);

/** QuerySpec field → column: flatten-prefix walk, then camelToSnake. THE shared
 *  translation for every transport (Node pg, browser PostgREST) — a divergent
 *  copy here was a review finding. */
export function fieldToColumn(def: TableDef, field: string, dialect: 'postgres' | 'postgrest' = 'postgres'): string {
  if (def.jsonTextQueries?.has(field)) {
    const [root, ...keys] = field.split('.');
    if (!root || !def.jsonb?.has(root) || !keys.length || [root, ...keys].some(key => !/^[A-Za-z_][A-Za-z0-9_]*$/.test(key))) {
      throw Error('Invalid declared JSON text query path');
    }
    const column = camelToSnake(root);
    return dialect === 'postgres' ? `${column} #>> '{${keys.join(',')}}'`
      : column + keys.map((key, index) => `${index === keys.length - 1 ? '->>' : '->'}${key}`).join('');
  }
  for (const [docField, nested] of Object.entries(def.flatten ?? {})) {
    if (field.startsWith(`${docField}.`)) {
      const col = nested[field.slice(docField.length + 1)];
      if (col) return col;
    }
  }
  return camelToSnake(field);
}
export const snakeToCamel = (s: string): string =>
  s.replace(/_([a-z0-9])/g, (_, c: string) => c.toUpperCase());
